import wireMapSchema from '../../../contracts/output-view/2/wire-map.schema.json';
import controlSchema from '../../../contracts/output-view/2/control.schema.json';

/**
 * output-view/2 byte codec: the binary frame messages that carry a literal copy of every datagram the Pi
 * put on the lighting wire, the wire map that says where each schematic element's intensity sits in them
 * (a DMX slot, or one argument of an OSC message to one destination), and the reading of an element's
 * level out of those bytes.
 *
 * Pure bytes in, values out: no peer connection, no signaling, no Solid state, no clock. The Go twin is
 * the controller's internal/outputview/codec, and both are held to the platform's golden vectors
 * (contracts/output-view/2/vectors, written by the controller's cmd/outputview-vectors). The layouts are
 * the platform's frame-message.schema.json and wire-map.schema.json x-binary sections; every multi-byte
 * integer is little-endian (OSC arguments inside a datagram are big-endian, as OSC defines them). u64/i64
 * fields are bigint because frame numbers outlive 2^53.
 */

export const FRAME_HEADER_BYTES = 24;
export const FRAME_ENTRY_HEADER_BYTES = 9;
export const MAX_FRAME_MESSAGE_BYTES = 16 * 1024;
export const MAP_ENTRY_BYTES = wireMapSchema['x-binary'].entryBytes;
export const MAX_MAP_ELEMENTS_PER_PART = wireMapSchema['x-binary'].elementsPerPart;
const wireMapControl = controlSchema.oneOf.find((entry) => entry.properties?.type?.const === 'wireMap');
if (!wireMapControl?.properties.parts?.maximum) throw new Error('output-view control contract has no wireMap parts ceiling');
export const MAX_MAP_PARTS = wireMapControl.properties.parts.maximum;
export const MAX_MAP_ELEMENTS = MAX_MAP_PARTS * MAX_MAP_ELEMENTS_PER_PART;
export const MAX_DESTINATIONS_BYTES = wireMapSchema['x-binary'].destinations.maximumBytes;
/**
 * Destination ips and addresses, and OSC address strings, are strict UTF-8, decoded byte for byte: a leading
 * BOM is kept (the controller's Go strings keep it), so it can never make two different addresses equal.
 */
const OSC_TEXT = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true });
const IPV4_OCTET = '(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])';
const IPV4_LITERAL = new RegExp(`^${IPV4_OCTET}(?:\\.${IPV4_OCTET}){3}$`);

/**
 * An IPv4 or IPv6 address literal with no zone, as the controller's netip.ParseAddr accepts it: dotted decimal
 * without leading zeros, or IPv6 (hex groups, `::`, an embedded IPv4 tail). Host names are refused.
 */
function isIpLiteral(text: string): boolean {
  if (IPV4_LITERAL.test(text)) return true;
  if (!text.includes(':') || !/^[0-9A-Fa-f:.]+$/.test(text)) return false;
  try {
    return new URL(`http://[${text}]/`).hostname.length > 2;
  } catch {
    return false;
  }
}
/** Partial frames held while their parts arrive; mirrors the Pi's 32-slot tap ring. */
export const MAX_PENDING_FRAMES = 32;

export const FLAG_BLACKOUT = 1;
export const FLAG_OVERFLOW = 2;

/** 1 sACN data, 2 sACN sync, 3 sACN stream-terminate, 4 Art-Net (ArtDmx or ArtSync), 5 OSC. */
export type DatagramProto = 1 | 2 | 3 | 4 | 5;
/** 0 no intensity channel at all (drawn as unknown), 1 sACN, 4 Art-Net, 5 OSC. */
export type MapProto = 0 | 1 | 4 | 5;

export interface WireDatagram {
  proto: DatagramProto;
  universe: number;
  offsetUs: number;
  data: Uint8Array;
}

export interface FrameMessageHeader {
  frameSeq: bigint;
  sentAtUs: bigint;
  part: number;
  parts: number;
  flags: number;
  count: number;
}

export interface WireFrame {
  frameSeq: bigint;
  sentAtUs: bigint;
  flags: number;
  parts: number;
  datagrams: WireDatagram[];
}

export class OutputViewDecodeError extends Error {}

function fail(message: string): never {
  throw new OutputViewDecodeError(`output-view: ${message}`);
}

function checkDatagram(proto: number, universe: number, length: number): void {
  if (proto < 1 || proto > 5) fail(`proto ${proto}`);
  if (length === 0) fail('empty datagram');
  if (proto <= 3 && (universe < 1 || universe > 63999)) fail(`sACN universe ${universe}`);
  if (proto === 4 && universe > 0x7fff) fail(`Art-Net port-address ${universe}`);
}

/** Parse one frame message. Datagram bytes are views into `message`. */
export function decodeFrameMessage(message: Uint8Array): { header: FrameMessageHeader; datagrams: WireDatagram[] } {
  if (message.length < FRAME_HEADER_BYTES || message.length > MAX_FRAME_MESSAGE_BYTES) fail(`${message.length}-byte message`);
  if (message[0] !== 0x4f || message[1] !== 0x56 || message[2] !== 1 || message[3] !== 1) fail('header');
  const view = new DataView(message.buffer, message.byteOffset, message.byteLength);
  const header: FrameMessageHeader = {
    frameSeq: view.getBigUint64(4, true),
    sentAtUs: view.getBigInt64(12, true),
    part: message[20],
    parts: message[21],
    flags: message[22],
    count: message[23],
  };
  if (header.parts === 0 || header.part >= header.parts) fail(`part ${header.part} of ${header.parts}`);
  if ((header.flags & ~(FLAG_BLACKOUT | FLAG_OVERFLOW)) !== 0) fail(`flags ${header.flags}`);
  if (header.count === 0) fail('no datagrams');
  const datagrams: WireDatagram[] = [];
  let at = FRAME_HEADER_BYTES;
  for (let index = 0; index < header.count; index += 1) {
    if (message.length - at < FRAME_ENTRY_HEADER_BYTES) fail(`entry ${index} truncated`);
    const proto = message[at];
    const universe = view.getUint16(at + 1, true);
    const offsetUs = view.getUint32(at + 3, true);
    const length = view.getUint16(at + 7, true);
    at += FRAME_ENTRY_HEADER_BYTES;
    if (message.length - at < length) fail(`entry ${index} overruns the message`);
    checkDatagram(proto, universe, length);
    if (index > 0 && offsetUs < datagrams[index - 1].offsetUs) fail(`entry ${index} offset decreases`);
    datagrams.push({ proto: proto as DatagramProto, universe, offsetUs, data: message.subarray(at, at + length) });
    at += length;
  }
  if (at !== message.length) fail(`${message.length - at} trailing bytes`);
  return { header, datagrams };
}

/**
 * Rebuilds complete frames from messages arriving in any order (the frame channel is unordered). A frame
 * is usable only when every part has arrived; a partial frame older than MAX_PENDING_FRAMES newer ones is
 * abandoned and counted, never drawn.
 */
export class FrameAssembler {
  private readonly pending = new Map<bigint, { header: FrameMessageHeader; parts: (WireDatagram[] | undefined)[]; got: number }>();
  incomplete = 0;

  /** Returns the completed frame when `message` was its last missing part, otherwise undefined. */
  add(message: Uint8Array): WireFrame | undefined {
    const { header, datagrams } = decodeFrameMessage(message);
    let entry = this.pending.get(header.frameSeq);
    if (!entry) {
      entry = { header, parts: new Array(header.parts).fill(undefined), got: 0 };
      this.pending.set(header.frameSeq, entry);
      this.evict();
    } else if (entry.header.parts !== header.parts || entry.header.sentAtUs !== header.sentAtUs || entry.header.flags !== header.flags) {
      fail(`frame ${header.frameSeq} parts disagree`);
    }
    if (entry.parts[header.part]) fail(`frame ${header.frameSeq} part ${header.part} repeated`);
    entry.parts[header.part] = datagrams;
    entry.got += 1;
    if (entry.got < header.parts) return undefined;
    this.pending.delete(header.frameSeq);
    const all = entry.parts.flatMap((part) => part ?? []);
    for (let index = 1; index < all.length; index += 1) {
      if (all[index].offsetUs < all[index - 1].offsetUs) fail(`frame ${header.frameSeq} offsets decrease across parts`);
    }
    return { frameSeq: header.frameSeq, sentAtUs: header.sentAtUs, flags: header.flags, parts: header.parts, datagrams: all };
  }

  get pendingFrames(): number {
    return this.pending.size;
  }

  private evict(): void {
    while (this.pending.size > MAX_PENDING_FRAMES) {
      let oldest: bigint | undefined;
      for (const seq of this.pending.keys()) if (oldest === undefined || seq < oldest) oldest = seq;
      this.pending.delete(oldest as bigint);
      this.incomplete += 1;
    }
  }
}

/**
 * proto 1/4: coarse/fine universe and slot. proto 5 (OSC): coarseUniverse is the destination index into
 * the map's destinations, coarseSlot the 0-based argument index, fine fields 0.
 */
export interface WireMapEntry {
  proto: MapProto;
  coarseUniverse: number;
  coarseSlot: number;
  fineUniverse: number;
  fineSlot: number;
}

/** One OSC (ip, port, address) the controller sends element values to. valueType 0 float32 0..1, 1 int32 0..65535. */
export interface OscDestination {
  ip: string;
  port: number;
  address: string;
  valueType: 0 | 1;
}

export interface WireMapMessage {
  type: 'wireMap';
  mapDigest: string;
  layoutDigest: string;
  elementCount: number;
  part: number;
  parts: number;
  entriesB64: string;
  /** Part 0 only: the packed destinations table. */
  destinationsB64?: string;
}

export interface WireMap {
  mapDigest: string;
  layoutDigest: string;
  entries: WireMapEntry[];
  destinations: OscDestination[];
}

function checkEntry(entry: WireMapEntry, index: number): void {
  const { proto, coarseUniverse, coarseSlot, fineUniverse, fineSlot } = entry;
  if (proto === 0) {
    if (coarseUniverse || coarseSlot || fineUniverse || fineSlot) fail(`element ${index}: unmapped element carries a location`);
    return;
  }
  if (proto === 5) {
    if (fineUniverse || fineSlot) fail(`element ${index}: OSC element names a fine slot`);
    return;
  }
  if (proto !== 1 && proto !== 4) fail(`element ${index}: proto ${proto}`);
  const universeOk = (u: number) => (proto === 1 ? u >= 1 && u <= 63999 : u <= 0x7fff);
  if (!universeOk(coarseUniverse) || coarseSlot < 1 || coarseSlot > 512) fail(`element ${index}: coarse ${coarseUniverse}/${coarseSlot}`);
  if (fineSlot === 0) {
    if (fineUniverse !== 0) fail(`element ${index}: 8-bit element names a fine universe`);
    return;
  }
  if (!universeOk(fineUniverse) || fineSlot > 512) fail(`element ${index}: fine ${fineUniverse}/${fineSlot}`);
}

/** Parse packed wire-map entries (elementCount × 9 bytes). */
export function decodeMapEntries(packed: Uint8Array): WireMapEntry[] {
  if (packed.length === 0 || packed.length % MAP_ENTRY_BYTES !== 0) fail(`${packed.length}-byte map`);
  const view = new DataView(packed.buffer, packed.byteOffset, packed.byteLength);
  const entries: WireMapEntry[] = [];
  for (let at = 0; at < packed.length; at += MAP_ENTRY_BYTES) {
    const entry: WireMapEntry = {
      proto: packed[at] as MapProto,
      coarseUniverse: view.getUint16(at + 1, true),
      coarseSlot: view.getUint16(at + 3, true),
      fineUniverse: view.getUint16(at + 5, true),
      fineSlot: view.getUint16(at + 7, true),
    };
    checkEntry(entry, at / MAP_ENTRY_BYTES);
    entries.push(entry);
  }
  return entries;
}

/**
 * Parse the packed destinations table: u16 count, then per destination u16-length ip, u16 port, u16-length
 * address, u8 valueType (little-endian lengths and port, UTF-8 strings). Trailing bytes, a repeated
 * destination or a field out of its contract range refuse the table.
 */
export function decodeDestinations(packed: Uint8Array): OscDestination[] {
  if (packed.length < 2 || packed.length > MAX_DESTINATIONS_BYTES) fail(`${packed.length}-byte destinations table`);
  const view = new DataView(packed.buffer, packed.byteOffset, packed.byteLength);
  const count = view.getUint16(0, true);
  let at = 2;
  const take = (bytes: number, what: string): Uint8Array => {
    if (packed.length - at < bytes) fail(`destination ${what} truncated`);
    const out = packed.subarray(at, at + bytes);
    at += bytes;
    return out;
  };
  const u16 = (what: string) => {
    const bytes = take(2, what);
    return bytes[0] | (bytes[1] << 8);
  };
  const text = (what: string, min: number, max: number) => {
    const length = u16(`${what} length`);
    if (length < min || length > max) fail(`destination ${what} of ${length} bytes`);
    try {
      return OSC_TEXT.decode(take(length, what));
    } catch (error) {
      if (error instanceof OutputViewDecodeError) throw error;
      fail(`destination ${what} is not UTF-8`);
    }
  };
  const destinations: OscDestination[] = [];
  const seen = new Set<string>();
  for (let index = 0; index < count; index += 1) {
    const ip = text('ip', 1, 45);
    if (!isIpLiteral(ip)) fail(`destination ${index}: ip ${JSON.stringify(ip)}`);
    const port = u16('port');
    if (port < 1) fail(`destination ${index}: port 0`);
    const address = text('address', 1, 255);
    if (!address.startsWith('/') || address.includes('\0')) fail(`destination ${index}: address ${JSON.stringify(address)}`);
    const valueType = take(1, 'valueType')[0];
    if (valueType !== 0 && valueType !== 1) fail(`destination ${index}: valueType ${valueType}`);
    const key = `${ip}\n${port}\n${address}`;
    if (seen.has(key)) fail(`destination ${index} repeated`);
    seen.add(key);
    destinations.push({ ip, port, address, valueType });
  }
  if (at !== packed.length) fail(`${packed.length - at} trailing destinations bytes`);
  return destinations;
}

function base64Bytes(value: string, field: string): Uint8Array {
  let raw: string;
  try {
    raw = atob(value);
  } catch {
    fail(`${field} is not base64`);
  }
  const out = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) out[index] = raw.charCodeAt(index);
  return out;
}

/** `sha256:` + lowercase hex SHA-256 of the packed map (entries, then the destinations table): the map digest the viewer echoes in `ready`. */
export async function mapDigestOf(packed: Uint8Array): Promise<string> {
  const copy = new Uint8Array(packed);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', copy));
  return `sha256:${Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Rebuild the map from all of its wireMap parts, in any order: the entry list from every part, the
 * destinations table from part 0. A map whose parts disagree, repeat, go missing, lack or misplace the
 * table, point at a destination outside it, or hash to anything but their declared digest is refused
 * whole: a wrong map is worse than a blank one. An output-view/1 map (no table) is refused.
 */
export async function assembleWireMap(messages: readonly WireMapMessage[]): Promise<WireMap> {
  const first = messages[0];
  if (!first) fail('no wireMap parts');
  const { mapDigest, layoutDigest, elementCount, parts } = first;
  if (!/^sha256:[0-9a-f]{64}$/.test(layoutDigest) || !/^sha256:[0-9a-f]{64}$/.test(mapDigest)) fail('digest shape');
  if (!Number.isInteger(elementCount) || elementCount < 1 || elementCount > MAX_MAP_ELEMENTS) fail(`elementCount ${elementCount}`);
  if (!Number.isInteger(parts) || parts < 1 || parts > MAX_MAP_PARTS
    || parts !== Math.ceil(elementCount / MAX_MAP_ELEMENTS_PER_PART) || messages.length !== parts) {
    fail(`${messages.length} of ${parts} parts`);
  }
  const chunks: (Uint8Array | undefined)[] = new Array(parts).fill(undefined);
  let table: Uint8Array | undefined;
  let totalBytes = 0;
  for (const message of messages) {
    if (message.type !== 'wireMap' || message.mapDigest !== mapDigest || message.layoutDigest !== layoutDigest ||
        message.elementCount !== elementCount || message.parts !== parts || !Number.isInteger(message.part) ||
        message.part < 0 || message.part >= parts) fail(`part ${message.part} disagrees`);
    if (chunks[message.part]) fail(`part ${message.part} repeated`);
    if ((message.part === 0) !== (typeof message.destinationsB64 === 'string')) fail(`part ${message.part}: destinations table misplaced`);
    if (message.part === 0) table = base64Bytes(message.destinationsB64 as string, 'destinationsB64');
    const chunk = base64Bytes(message.entriesB64, 'entriesB64');
    const expectedEntries = Math.min(MAX_MAP_ELEMENTS_PER_PART, elementCount - message.part * MAX_MAP_ELEMENTS_PER_PART);
    if (chunk.length !== expectedEntries * MAP_ENTRY_BYTES) fail(`part ${message.part} carries ${chunk.length} bytes`);
    totalBytes += chunk.length;
    chunks[message.part] = chunk;
  }
  if (totalBytes !== elementCount * MAP_ENTRY_BYTES) fail(`${totalBytes}-byte map for ${elementCount} elements`);
  const destinationsBytes = table as Uint8Array;
  const packed = new Uint8Array(elementCount * MAP_ENTRY_BYTES + destinationsBytes.length);
  let at = 0;
  for (const chunk of chunks) {
    packed.set(chunk as Uint8Array, at);
    at += (chunk as Uint8Array).length;
  }
  packed.set(destinationsBytes, at);
  if ((await mapDigestOf(packed)) !== mapDigest) fail('entries and destinations do not hash to mapDigest');
  const entries = decodeMapEntries(packed.subarray(0, at));
  const destinations = decodeDestinations(destinationsBytes);
  entries.forEach((entry, index) => {
    if (entry.proto === 5 && entry.coarseUniverse >= destinations.length) fail(`element ${index}: destination ${entry.coarseUniverse} of ${destinations.length}`);
  });
  return { mapDigest, layoutDigest, entries, destinations };
}

/**
 * One element's level as it went out on the wire, in the golden vectors' shape (the controller's codec.Level):
 * a DMX value (bits 8 or 16, raw / 255 or raw / 65535), an OSC argument already scaled to 0..1 by its
 * destination's valueType (bits 0 with `fraction`; OSC has no 8/16-bit form), or bits 0 alone when the
 * element has no level in this frame.
 */
export type WireLevel =
  | { raw: number; bits: 8 | 16 }
  | { raw: 0; bits: 0; fraction: number }
  | { raw: 0; bits: 0 };

const NO_LEVEL: WireLevel = { raw: 0, bits: 0 };

/** The drawn fraction 0..1 of a level, or undefined when the element has no level in this frame. */
export function levelFraction(level: WireLevel): number | undefined {
  if ('fraction' in level) return level.fraction;
  if (level.bits === 0) return undefined;
  return level.raw / (level.bits === 8 ? 255 : 65535);
}

const ART_NET_ID = [0x41, 0x72, 0x74, 0x2d, 0x4e, 0x65, 0x74, 0x00];

function artDmxSlots(data: Uint8Array): Uint8Array | undefined {
  if (data.length < 18 || ART_NET_ID.some((byte, index) => data[index] !== byte)) return undefined;
  if ((data[8] | (data[9] << 8)) !== 0x5000) return undefined;
  const length = Math.min((data[16] << 8) | data[17], data.length - 18);
  return data.subarray(18, 18 + length);
}

interface OscArgument {
  tag: 'f' | 'i';
  value: number;
}

/** The OSC string at `at` (NUL-terminated, padded to 4 bytes): its text and the offset after the padding. */
function oscString(data: Uint8Array, at: number): { text: string; next: number } | undefined {
  const end = data.indexOf(0, at);
  if (end < 0) return undefined;
  let text: string;
  try {
    text = OSC_TEXT.decode(data.subarray(at, end));
  } catch {
    return undefined;
  }
  return { text, next: end + 4 - ((end - at) % 4) };
}

/**
 * The leading f/i arguments of one OSC message to `address` (big-endian), stopping at the first other
 * type tag; undefined for a bundle, another address, or a malformed message.
 */
function oscArguments(data: Uint8Array, address: string): OscArgument[] | undefined {
  const path = oscString(data, 0);
  if (!path || path.text !== address || path.next > data.length) return undefined;
  const tags = oscString(data, path.next);
  if (!tags || !tags.text.startsWith(',') || tags.next > data.length) return undefined;
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const args: OscArgument[] = [];
  let at = tags.next;
  for (const tag of tags.text.slice(1)) {
    if ((tag !== 'f' && tag !== 'i') || data.length - at < 4) break;
    args.push({ tag, value: tag === 'f' ? view.getFloat32(at, false) : view.getInt32(at, false) });
    at += 4;
  }
  return args;
}

function oscLevel(destination: OscDestination | undefined, args: OscArgument[] | undefined, index: number): WireLevel {
  const arg = args?.[index];
  if (!destination || !arg || arg.tag !== (destination.valueType === 0 ? 'f' : 'i') || Number.isNaN(arg.value)) return NO_LEVEL;
  const value = destination.valueType === 0 ? arg.value : arg.value / 65535;
  return { raw: 0, bits: 0, fraction: Math.min(1, Math.max(0, value)) };
}

/**
 * Read every element's level out of one complete frame: sACN data packets with the null start code
 * (slot s at datagram offset 125 + s), ArtDmx packets (slot s at offset 17 + s, within the declared
 * length), and OSC messages, which name their destination in the datagram's universe field: argument
 * argumentIndex of the message to that destination's address, scaled by its valueType. The last datagram
 * for a universe (or destination) wins.
 */
export function elementLevels(map: WireMap, frame: WireFrame): WireLevel[] {
  const sacn = new Map<number, Uint8Array>();
  const artnet = new Map<number, Uint8Array>();
  const osc = new Map<number, OscArgument[] | undefined>();
  for (const datagram of frame.datagrams) {
    if (datagram.proto === 1 && datagram.data.length > 125 && datagram.data[125] === 0) {
      sacn.set(datagram.universe, datagram.data.subarray(126));
    } else if (datagram.proto === 4) {
      const slots = artDmxSlots(datagram.data);
      if (slots) artnet.set(datagram.universe, slots);
    } else if (datagram.proto === 5 && datagram.universe < map.destinations.length) {
      osc.set(datagram.universe, oscArguments(datagram.data, map.destinations[datagram.universe].address));
    }
  }
  const slot = (source: Map<number, Uint8Array>, universe: number, index: number): number | undefined => {
    const data = source.get(universe);
    return data && index >= 1 && index <= data.length ? data[index - 1] : undefined;
  };
  return map.entries.map((entry): WireLevel => {
    if (entry.proto === 5) return oscLevel(map.destinations[entry.coarseUniverse], osc.get(entry.coarseUniverse), entry.coarseSlot);
    const source = entry.proto === 1 ? sacn : entry.proto === 4 ? artnet : undefined;
    if (!source) return { raw: 0, bits: 0 };
    const coarse = slot(source, entry.coarseUniverse, entry.coarseSlot);
    if (coarse === undefined) return { raw: 0, bits: 0 };
    if (entry.fineSlot === 0) return { raw: coarse, bits: 8 };
    const fine = slot(source, entry.fineUniverse, entry.fineSlot);
    return fine === undefined ? { raw: 0, bits: 0 } : { raw: coarse * 256 + fine, bits: 16 };
  });
}
